import { useState } from "react";
import { Badge, Button, Card, Col, Drawer, Flex, Row, Typography } from "antd";
import { ArrowRightOutlined, MenuOutlined } from "@ant-design/icons";
import { Link, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { routes } from "../../routes.ts";
import { LanguageSwitcher } from "../../components/LanguageSwitcher.tsx";
import { PricingCards } from "../../components/PricingCards.tsx";
import LogoSvg from "../../assets/logo.svg?react";
import heroImg from "../../assets/landing/hero.jpg";
import {
  desktopActions,
  drawerClose,
  drawerLink,
  featureCard,
  featuresStyle,
  featuresTitle,
  footer,
  header,
  headerInner,
  headerLeft,
  hero,
  heroCta,
  heroImage,
  heroInner,
  heroOverlay,
  heroSignIn,
  heroSubtitle,
  heroTitle,
  logoIcon,
  logoLink,
  logoText,
  mobileDrawer,
  mobileMenuBtn,
  nav,
  navLink,
  page,
  pricing,
  pricingInner,
  pricingSubtitle,
  pricingTitle,
} from "./LandingPage.styles.ts";
import { footerLine, footerSeparator } from "../legal/LegalPage.styles.ts";
import recipesImg from "../../assets/landing/feature-recipes.jpg";
import nutritionImg from "../../assets/landing/feature-nutrition.jpg";
import purchasesImg from "../../assets/landing/feature-purchases.jpg";
import wineLabelsImg from "../../assets/landing/feature-wine-labels.jpg";
import qrLabelsImg from "../../assets/landing/feature-qr-labels.jpg";
import mealPlanningImg from "../../assets/landing/feature-meal-planning.jpg";
import type { UserFragment } from "../../gql/generated/graphqlSchema.tsx";

const { Title, Paragraph, Text } = Typography;

type Props = {
  readonly authLoading?: boolean;
  readonly currentLoggedUser: UserFragment | null | undefined;
};

const features = ["recipes", "nutrition", "purchases", "wineLabels", "qrLabels", "mealPlanning"] as const;
const featureImages: Record<(typeof features)[number], string> = {
  recipes: recipesImg,
  nutrition: nutritionImg,
  purchases: purchasesImg,
  wineLabels: wineLabelsImg,
  qrLabels: qrLabelsImg,
  mealPlanning: mealPlanningImg,
};
const comingSoonFeatures: ReadonlySet<(typeof features)[number]> = new Set([
  "purchases",
  "wineLabels",
  "qrLabels",
  "mealPlanning",
]);

export const LandingPage = ({ authLoading = false, currentLoggedUser }: Props) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const headerButtons = currentLoggedUser ? (
    <Link to={routes.recipes.to()}>
      <Button icon={<ArrowRightOutlined />} iconPlacement="end" type="primary">
        {t("landing.openApp")}
      </Button>
    </Link>
  ) : authLoading ? (
    <Button disabled loading type="primary">
      {t("landing.openApp")}
    </Button>
  ) : (
    <>
      <Link to={routes.login.to()}>
        <Button>{t("landing.signIn")}</Button>
      </Link>
      <Link to={routes.login.to()}>
        <Button type="primary">{t("landing.getStarted")}</Button>
      </Link>
    </>
  );

  return (
    <div className={page}>
      {/* Header */}
      <div className={header}>
        <Flex align="center" className={headerInner} justify="space-between">
          <Flex align="center" className={headerLeft}>
            <Flex align="center" className={logoLink}>
              <LogoSvg className={logoIcon} />
              <Text className={logoText} strong>
                Calculabel
              </Text>
            </Flex>
            <nav className={nav}>
              <a className={navLink} href="#features">
                {t("landing.navFeatures")}
              </a>
              <a className={navLink} href="#pricing">
                {t("landing.navPricing")}
              </a>
            </nav>
          </Flex>
          <Flex align="center" className={desktopActions}>
            <LanguageSwitcher collapsed />
            {headerButtons}
          </Flex>
          <Button className={mobileMenuBtn} icon={<MenuOutlined />} onClick={() => setDrawerOpen(true)} />
        </Flex>
      </div>
      <Drawer
        className={mobileDrawer}
        classNames={{ close: drawerClose }}
        destroyOnHidden
        onClose={() => setDrawerOpen(false)}
        open={drawerOpen}
        placement="right"
        size={260}
        title={
          <Flex align="center" className={logoLink}>
            <LogoSvg className={logoIcon} />
            <Text className={logoText} strong>
              Calculabel
            </Text>
          </Flex>
        }
      >
        <Flex gap="small" vertical>
          {headerButtons}
          <a className={drawerLink} href="#features" onClick={() => setDrawerOpen(false)}>
            {t("landing.navFeatures")}
          </a>
          <a className={drawerLink} href="#pricing" onClick={() => setDrawerOpen(false)}>
            {t("landing.navPricing")}
          </a>
          <LanguageSwitcher />
        </Flex>
      </Drawer>
      {/* Hero */}
      <div className={hero}>
        <img alt="" className={heroImage} src={heroImg} />
        <div className={heroOverlay} />
        <div className={heroInner}>
          <Title className={heroTitle}>{t("landing.heroTitle")}</Title>
          <Paragraph className={heroSubtitle}>{t("landing.heroSubtitle")}</Paragraph>
          <Flex className={heroCta} justify="center">
            {currentLoggedUser ? (
              <Link to={routes.recipes.to()}>
                <Button icon={<ArrowRightOutlined />} iconPlacement="end" size="large" type="primary">
                  {t("landing.openApp")}
                </Button>
              </Link>
            ) : authLoading ? (
              <Button disabled loading size="large" type="primary">
                {t("landing.openApp")}
              </Button>
            ) : (
              <>
                <Link to={routes.login.to()}>
                  <Button size="large" type="primary">
                    {t("landing.getStartedFree")}
                  </Button>
                </Link>
                <Link to={routes.login.to()}>
                  <Button className={heroSignIn} size="large">
                    {t("landing.signIn")}
                  </Button>
                </Link>
              </>
            )}
          </Flex>
        </div>
      </div>

      {/* Features */}
      <div className={featuresStyle} id="features">
        <Title className={featuresTitle} level={2}>
          {t("landing.featuresTitle")}
        </Title>
        <Row gutter={[24, 24]}>
          {features.map((key) => {
            const card = (
              <Card
                className={featureCard}
                cover={<img alt={t(`landing.features.${key}.title`)} src={featureImages[key]} />}
              >
                <Title level={4}>{t(`landing.features.${key}.title`)}</Title>
                <Paragraph type="secondary">{t(`landing.features.${key}.description`)}</Paragraph>
              </Card>
            );
            return (
              <Col key={key} md={8} sm={12} xs={24}>
                {comingSoonFeatures.has(key) ? (
                  <Badge.Ribbon color="orange" text={t("landing.comingSoon")}>
                    {card}
                  </Badge.Ribbon>
                ) : (
                  card
                )}
              </Col>
            );
          })}
        </Row>
      </div>

      {/* Pricing */}
      <div className={pricing} id="pricing">
        <div className={pricingInner}>
          <Title className={pricingTitle} level={2}>
            {t("landing.pricingTitle")}
          </Title>
          <Paragraph className={pricingSubtitle} type="secondary">
            {t("landing.pricingSubtitle")}
          </Paragraph>
          <Flex justify="center">
            <PricingCards
              ctaIcon={<ArrowRightOutlined />}
              ctaIconPlacement="end"
              ctaLabel={t("pricing.startTrial")}
              onSelect={() => navigate(routes.login.to())}
              showStripeNote={false}
            />
          </Flex>
        </div>
      </div>

      {/* Footer */}
      <div className={footer}>
        <div className={footerLine}>
          <Text type="secondary">{t("landing.footer")}</Text>
          <span className={footerSeparator}>·</span>
          <Link to={routes.legal.to()}>{t("common.legal")}</Link>
          <span className={footerSeparator}>·</span>
          <Link to={routes.privacy.to()}>{t("common.privacy")}</Link>
          <span className={footerSeparator}>·</span>
          <Link to={routes.terms.to()}>{t("common.terms")}</Link>
        </div>
      </div>
    </div>
  );
};
